/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  category: 'ALL',
  subcat: 'ALL',
  customer: 'ALL',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function SearchCustomer({ callback }) {
  const mod = 'invoice';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalOpen, setModalOpen] = useState(false);
  const txtSearch = useRef(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'tb.code', label: t(`attr.code`), align: 'left', width: 100 },
    { id: 'tb.name', label: 'ลูกค้า', align: 'left' },
    { id: 'tb.count_bill', label: 'ใบแจ้งหนี้', align: 'right', sort: false },
    { id: 'tb.last_bill', label: 'เปิดล่าสุด', align: 'center', sort: false },
    { id: 'tb.credit_day', label: 'เครดิตวัน', align: 'right' },
    { id: 'tb.credit_money', label: 'เครดิตวงเงิน', align: 'right' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('OPTIONS', `/${mod}/customer`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      customer: data.customer,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const btnOpen = (item) => {
    return (
      <Tooltip title='สร้างใบวางบิล'>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap', textAlign: 'left', p: 0, display: 'block' }}
          onClick={() => {
            setModalOpen(false);
            callback({ customer_id: item.id });
          }}
        >
          {item.code}
        </MDButton>
      </Tooltip>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const renderSearch = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  inputRef: txtSearch,
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
              <Table aria-label='simple table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{Func.imageDisplay(row)}</TableCell>
                      <TableCell align='left' className='nowrap'>
                        {btnOpen(row)}
                      </TableCell>
                      <TableCell align='left'>
                        <MDBox className='cut' width={350}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap color-danger'>
                        {row.count_bill > 0 ? Func.numberDisplay(row.count_bill) : '-'}
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.last_bill}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {row.credit_day > 0 ? row.credit_day : '-'}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {row.credit_money > 0 ? Func.numberDisplay(row.credit_money) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          เพิ่มใบวางบิล
          <IconButton
            aria-label='close'
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      <MDButton
        variant='contained'
        color='dark'
        size='large'
        fullWidth
        sx={{ pt: 1.6, pb: 1.6 }}
        onClick={() => {
          setModalOpen(true);
          view(find);

          setTimeout(function () {
            txtSearch.current.focus();
          }, 500);
        }}
      >
        <Icon>post_add</Icon>
      </MDButton>

      {renderOpen()}
      {notify}
    </div>
  );
}

SearchCustomer.propTypes = {
  callback: PropTypes.func.isRequired,
};

export default SearchCustomer;
